import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscription } from "@/hooks/useSubscription";

/** Os três estados possíveis de acesso de quem ainda não assinou. */
export type FirstEventState = "full" | "criar" | "leitura";

/**
 * Regra única (não espalhada):
 *
 *   full    → assinatura ativa. Acesso normal.
 *   criar   → sem assinatura e o primeiro cronograma ainda não existe.
 *             Pode fazer o onboarding, criar o primeiro evento, preencher o
 *             briefing e gerar as tarefas.
 *   leitura → sem assinatura e o primeiro cronograma já existe. Pode abrir e
 *             ver as telas do primeiro evento, sem alterar nada. Sem prazo.
 *
 * Desde 30/09/2026 todo cadastro nasce em trialing (teste de 14 dias sem
 * cartão) → "full". Quando o teste vence (canceled) → "leitura", sem prazo.
 */
export function useFirstEventAccess() {
  const { user, loading: authLoading } = useAuth();
  const { isActive, loading: subLoading } = useSubscription();

  const enabled = !!user && !authLoading && !subLoading && !isActive;

  const { data, isLoading } = useQuery({
    queryKey: ["first-event-access", user?.id],
    enabled,
    staleTime: 30_000,
    queryFn: async () => {
      const { data: eventos, error } = await supabase
        .from("eventos")
        .select("id")
        .eq("usuario_id", user!.id)
        .order("created_at", { ascending: true })
        .limit(1);

      if (error) throw error;

      const firstEventId = eventos?.[0]?.id ?? null;
      if (!firstEventId) {
        return { firstEventId: null, firstScheduleDone: false };
      }

      const { data: tasks, error: tasksError } = await supabase
        .from("event_tasks")
        .select("id")
        .eq("event_id", firstEventId)
        .limit(1);

      if (tasksError) throw tasksError;

      return {
        firstEventId,
        firstScheduleDone: (tasks ?? []).length > 0,
      };
    },
  });

  const firstScheduleDone = data?.firstScheduleDone ?? false;
  const loading = authLoading || subLoading || (enabled && isLoading);

  let state: FirstEventState;
  if (isActive) state = "full";
  else if (firstScheduleDone) state = "leitura";
  else state = "criar";

  return {
    state,
    firstEventId: data?.firstEventId ?? null,
    firstScheduleDone,
    isActive,
    /** Pode criar/editar: assinatura ativa ou primeiro cronograma ainda não criado. */
    allowed: state === "full" || state === "criar",
    /** Só leitura das telas do primeiro evento. */
    readOnly: state === "leitura",
    loading,
  };
}
